import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SupportedLanguage, isSupportedLanguage, DEFAULT_LANGUAGE } from '@/i18n';
import { getAccessToken } from '@/services/api';
import { devLog } from '@/utils/logger';

/**
 * Language Context
 *
 * Manages language/locale state for the application:
 * - Provides current language and available languages
 * - Handles language switching with backend sync
 * - Persists language preference in localStorage
 * - Syncs preference with backend for authenticated users
 *
 * Usage:
 * ```tsx
 * const { currentLanguage, languages, changeLanguage } = useLanguage();
 * ```
 */

interface Language {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  isDefault: boolean;
}

interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  languages: Language[];
  changeLanguage: (lang: SupportedLanguage) => Promise<void>;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Language metadata for UI display
const LANGUAGE_INFO: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', isDefault: true },
  { code: 'de', name: 'German', nativeName: 'Deutsch', isDefault: false },
  { code: 'fr', name: 'French', nativeName: 'Français', isDefault: false },
  { code: 'es', name: 'Spanish', nativeName: 'Español', isDefault: false },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', isDefault: false },
];

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  // Get current language with fallback to default
  const currentLanguage = (isSupportedLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE) as SupportedLanguage;

  /**
   * Change language with backend sync
   *
   * Flow:
   * 1. Validate language is supported
   * 2. Update i18n language (triggers translation reload)
   * 3. Store preference in localStorage
   * 4. If user is authenticated, sync with backend
   *
   * @param lang - Language code to switch to
   */
  const changeLanguage = useCallback<(lang: SupportedLanguage) => Promise<void>>(
    async (lang: SupportedLanguage) => {
      // Validate language
      if (!isSupportedLanguage(lang)) {
        devLog.warn(`[LanguageContext] Unsupported language: ${lang}`);
        return;
      }

      setIsLoading(true);
      try {
        // Update i18n language (this reloads translations)
        await i18n.changeLanguage(lang);

        // Store preference in localStorage
        localStorage.setItem('preferredLanguage', lang);
        devLog.log(`[LanguageContext] Language changed to: ${lang}`);

        // Sync with backend if user is authenticated
        const token = getAccessToken();
        if (token) {
          try {
            const response = await fetch('/api/i18n/user/preference', {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ language: lang }),
            });

            if (!response.ok) {
              devLog.warn('[LanguageContext] Failed to sync language preference with server:', response.status);
            } else {
              devLog.log('[LanguageContext] Language preference synced with server');
            }
          } catch (error) {
            // Non-critical error - don't block language change
            devLog.warn('[LanguageContext] Failed to sync language preference with server:', error);
          }
        }
      } catch (error) {
        devLog.error('[LanguageContext] Failed to change language:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [i18n]
  );

  /**
   * Initialize language on mount
   * - i18n auto-detects from localStorage or browser
   * - We just log the detected language
   */
  useEffect(() => {
    devLog.log(`[LanguageContext] Initialized with language: ${currentLanguage}`);
  }, []); // Empty deps - only run once on mount

  const value: LanguageContextType = {
    currentLanguage,
    languages: LANGUAGE_INFO,
    changeLanguage,
    isLoading,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/**
 * Hook to access language context
 *
 * @throws Error if used outside LanguageProvider
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
